import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../api/http.api';
import TranscriptEditor from '../../components/TranscriptEditor';
import { LoginForm } from '../login/LoginForm';
import { useAuth } from '../../hooks/useAuth';
import { groupBlocksBySpeaker } from '../../components/TranscriptEditor/utils/transcriptOperations';
import TranscriptService from '../../services/transcript/TranscriptService';
import '../../components/TranscriptEditor/TranscriptEditor.css';

/**
 * TranscriptEditor Page
 * 
 * Advanced transcript editing interface for sessions
 */
const TranscriptEditorPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, error: authError, getAuth } = useAuth();

  const [sessionData, setSessionData] = useState(null);
  const [transcriptBlocks, setTranscriptBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Language persisted in URL ?lang=xx — read only (switching is disabled in editor)
  const urlLang = searchParams.get('lang') || 'en';
  const currentLanguage = urlLang;
  const languageRef = useRef(urlLang);
  languageRef.current = urlLang;
  const fetchGenRef = useRef(0);

  /**
   * Fetch session and transcript data
   */
  useEffect(() => {
    if (!user || !sessionId) return;
    const myGen = ++fetchGenRef.current;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch session data
        const sessionRes = await apiClient.get(`/api/reports/sessions/${sessionId}`);
        if (myGen !== fetchGenRef.current) return; // stale - language switch happened
        const session = sessionRes.data;
        setSessionData(session);

        // Check if transcript is available for editing
        // Only allow editing when transcript is COMPLETED
        const transcriptStatus = session.transcriptStatus || session.transcript_status;
        
        if (transcriptStatus !== 'COMPLETED') {
          const statusMessages = {
            'PENDING': 'Transcript processing has not started yet.',
            'PROCESSING': 'Transcript is still being processed. Please wait until processing is complete.',
            'FAILED': 'Transcript processing failed. Please retry processing.',
          };
          
          const message = statusMessages[transcriptStatus] || 'Transcript is not available for editing yet.';
          setError(`${message}\n\nTranscript editing is only available when processing is fully completed.`);
          setLoading(false);
          return;
        }

        // Fetch transcript data
        try {
          const activeLang = languageRef.current;
          let transcriptSegments = [];
          let usedPagedEndpoint = false;

          try {
            const firstPage = await TranscriptService.getTranscriptSegments(sessionId, 0, 500, activeLang);
            if (firstPage.success && Array.isArray(firstPage.data?.segments)) {
              usedPagedEndpoint = true;
              transcriptSegments = firstPage.data.segments;

              let offset = firstPage.data.offset + firstPage.data.limit;
              const total = firstPage.data.total;
              const hardCap = 20000;

              while (offset < total && transcriptSegments.length < hardCap) {
                const page = await TranscriptService.getTranscriptSegments(sessionId, offset, 500, activeLang);
                if (!page.success || !Array.isArray(page.data?.segments) || page.data.segments.length === 0) {
                  break;
                }
                transcriptSegments = transcriptSegments.concat(page.data.segments);
                offset += page.data.limit;
              }
            }
          } catch (e) {
            usedPagedEndpoint = false;
          }

          if (myGen !== fetchGenRef.current) return; // stale

          const transcriptRes = usedPagedEndpoint
            ? { data: { segments: transcriptSegments } }
            : await apiClient.get(`/api/reports/transcripts/${sessionId}`, { params: { lang: activeLang } });

          if (myGen !== fetchGenRef.current) return; // stale

          // Convert transcript segments to editor block format
          const blocks = (transcriptRes.data.segments || []).map((segment, index) => ({
            id: segment.id || `segment-${index}`,
            text: segment.text || '',
            speaker: segment.speaker || 'UNKNOWN',
            start: segment.start || 0,
            end: segment.end || 0,
            words: segment.words || [], // ✅ Include word-level timing data
            paragraphNumber: segment.paragraph_number || index,
            metadata: {
              isEdited: segment.is_edited || false,
              lastModified: segment.last_modified || new Date().toISOString(),
              version: segment.version || 1,
            },
          }));

          // Debug: Log processed blocks
          console.log('[TranscriptEditor] Processed blocks:', {
            blockCount: blocks.length,
            firstBlock: blocks[0],
            hasWords: !!blocks[0]?.words,
            wordCount: blocks[0]?.words?.length
          });

          // Apply intelligent grouping by speaker (8-10 sentences per block)
          const groupedBlocks = groupBlocksBySpeaker(blocks);
          
          // Debug: Log grouped blocks
          console.log('[TranscriptEditor] Grouped blocks:', {
            groupedCount: groupedBlocks.length,
            firstGrouped: groupedBlocks[0],
            hasWords: !!groupedBlocks[0]?.words,
            wordCount: groupedBlocks[0]?.words?.length
          });

          setTranscriptBlocks(groupedBlocks);
        } catch (transcriptError) {
          // If transcript doesn't exist yet, start with empty blocks
          console.log('No transcript found yet:', transcriptError);
          setTranscriptBlocks([]);
        }
      } catch (err) {
        console.error('Error fetching session data:', err);
        setError(err.message || 'Failed to load session');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, sessionId]);

  // Show login form if not authenticated
  if (!user) {
    return (
      <LoginForm
        onSSOLogin={getAuth}
        ssoLoading={authLoading}
        ssoError={authError}
      />
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="te-page-state">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-gray-700 mx-auto mb-4"></div>
          <p style={{ color: '#71717a', fontSize: 13 }}>Loading session...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="te-page-state">
        <div className="text-center max-w-md" style={{ padding: 24 }}>
          <h1 style={{ color: '#18181b', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Cannot edit transcript</h1>
          <p style={{ color: '#71717a', fontSize: 13.5, marginBottom: 16, whiteSpace: 'pre-line' }}>{error}</p>
          <button
            onClick={() => navigate('/sessions')}
            className="te-btn te-btn-secondary"
          >
            Back to sessions
          </button>
        </div>
      </div>
    );
  }

  // Show no transcript state
  if (!transcriptBlocks || transcriptBlocks.length === 0) {
    return (
      <div className="te-page-state">
        <div className="text-center max-w-md" style={{ padding: 24 }}>
          <h1 style={{ color: '#18181b', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No transcript</h1>
          <p style={{ color: '#71717a', fontSize: 13.5, marginBottom: 16 }}>
            This session doesn't have a transcript yet. Please generate one first.
          </p>
          <button
            onClick={() => navigate(`/sessions/${sessionId}`)}
            className="te-btn te-btn-secondary"
          >
            Go to session details
          </button>
        </div>
      </div>
    );
  }

  // Get video URL from session data
  const videoUrl = sessionData?.videoUrl || sessionData?.video_url || sessionData?.file_path;
  
  // If videoUrl is relative, construct full URL
  let fullVideoUrl = videoUrl && !videoUrl.startsWith('http') 
    ? `${process.env.REACT_APP_API_BASE_URL || 'https://reporting-api.diplomacy.edu'}${videoUrl}` 
    : videoUrl;
  
  // Add token as query parameter for streaming authentication
  // This allows HTML5 video element to stream directly using Range requests
  if (fullVideoUrl && fullVideoUrl.includes('/api/')) {
    const token = localStorage.getItem('diploAccessKey');
    if (token) {
      const separator = fullVideoUrl.includes('?') ? '&' : '?';
      fullVideoUrl = `${fullVideoUrl}${separator}token=${encodeURIComponent(token)}`;
    }
  }

  return (
    <TranscriptEditor
      key={currentLanguage}
      sessionId={sessionId}
      videoUrl={fullVideoUrl}
      initialBlocks={transcriptBlocks}
      sessionTitle={sessionData?.title}
      backUrl={`/sessions/${sessionId}`}
    />
  );
};

export default TranscriptEditorPage;


